import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {autoUnsubscribe} from "../../../core/decorators/auto-unsubscribe.decorator";
import {CollectionRevision} from "../../../core/models/collection-revision";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {CompareFilesDialog, CompareFilesDialogData} from "./compare-files.dialog";
import {RevisionsService} from "../../../core/api/revisions.service";
import {first, tap, zip} from "rxjs";
import {MatTable} from "@angular/material/table";
import {FileUtilsService} from "../../../core/services/file-utils.service";
import {ModelFoldersComponent} from "../../pages/collections/view-collection/model-folders.component";
import {environment} from "../../../../environments/environment";
import {CollectionFile} from "../../../core/models/collection-file";
import { autoMonitorPageView } from "src/app/core/decorators/auto-monitor-page-view.decorator";
import { MonitoringService } from "src/app/core/services/monitoring.service";


export interface CompareCollectionsDialogData {
  leftRevision: CollectionRevision,
  rightRevision: CollectionRevision
}


interface FileComparison {
  idx: number,
  leftPath: string,
  leftName: string,
  leftSize: number,
  leftFileId: string,
  leftFolder: string,
  leftDate: Date,
  rightPath: string,
  rightName: string,
  rightSize: number,
  rightFileId: string,
  rightFolder: string,
  rightDate: Date,
  changeType: 'no-change' | 'modified' | 'deleted' | 'added',
  comparisonViewable: boolean
}

@autoMonitorPageView({ name: 'Compare Collections Dialog', trackOnInitToAfterInit: false })
@Component({
  standalone: false,
  selector: 'app-compare-collections-dialog',
  templateUrl: './compare-collections.dialog.html',
  styleUrls: ['./compare-collections.dialog.scss']
})
@autoUnsubscribe({autoInclude: true})
export class CompareCollectionsDialog implements OnInit, AfterViewInit, OnDestroy{
  inProgress = true;
  fileComparisons: FileComparison[] = [];
  displayedColumns: string[] = ['idx', 'leftName', 'leftFolder', 'leftSize', 'leftDate', 'rightName', 'rightFolder', 'rightSize', 'rightDate'];
  @ViewChild(MatTable) table: MatTable<FileComparison>;
  private static readonly viewableExtensions = ['manifest', 'afi', 'csv', 'ixf', 'py'];

  constructor(@Inject(MAT_DIALOG_DATA) public data: CompareCollectionsDialogData, private revisionsService: RevisionsService,
              private fileUtilsService: FileUtilsService, private dialog: MatDialog, private monitoringService: MonitoringService) {
  }
  ngAfterViewInit(): void {
    // do nothing
  }

  ngOnInit(): void {
    this.inProgress = true;
    this.fileComparisons = [];
    zip(
      this.revisionsService.getFilesForRevision(this.data.leftRevision.id).pipe(first()),
      this.revisionsService.getFilesForRevision(this.data.rightRevision.id).pipe(first())
    ).pipe(first(), tap(([leftCollectionFiles, rightCollectionFiles]) => {
      leftCollectionFiles.forEach(collFile => {
        const rightFile = rightCollectionFiles.find(p => collFile.path === p.path);
        const comparisonViewable = environment.viewableExtensions.some(p => p === this.fileUtilsService.getFileExtension(collFile.path));
        const fileComparison: FileComparison = {
          idx: this.fileComparisons.length + 1,
          leftPath: collFile.path,
          leftName: this.fileUtilsService.getFileName(collFile.path),
          leftFileId: collFile.id,
          leftFolder: this.fileUtilsService.getParentFolderPath(collFile.path),
          leftSize: collFile.size / 1024,
          leftDate: collFile.fileModifiedDateTime,
          rightName: rightFile && this.fileUtilsService.getFileName(rightFile.path),
          rightFileId: rightFile?.id,
          rightFolder: rightFile && this.fileUtilsService.getParentFolderPath(rightFile.path),
          rightSize: !!rightFile ? rightFile.size / 1024 : null,
          rightPath: rightFile?.path,
          rightDate: collFile.fileModifiedDateTime,
          changeType: this.getChangeType(rightFile, collFile),
          //changeType !rightFile ? 'deleted' : (collFile.md5 === rightFile.md5)? 'no-change': 'modified'
          comparisonViewable
        };
        this.fileComparisons.push(fileComparison);
      });

      rightCollectionFiles.forEach(collFile => {
        const rightFileExist = this.fileComparisons.find(p => collFile.path === p.leftPath) as FileComparison;
        if(!rightFileExist) {
          const comparisonViewable = environment.viewableExtensions.some(p => p === this.fileUtilsService.getFileExtension(collFile.path));
          this.fileComparisons.push({
            idx: this.fileComparisons.length + 1,
            leftPath: null,
            leftName: null,
            leftFileId: null,
            leftFolder: null,
            leftSize: null,
            leftDate: null,
            rightName: this.fileUtilsService.getFileName(collFile.path),
            rightFileId: collFile.id,
            rightFolder: this.fileUtilsService.getParentFolderPath(collFile.path),
            rightSize: collFile.size / 1024,
            rightPath: collFile.path,
            rightDate: collFile.fileModifiedDateTime,
            changeType: 'added',
            comparisonViewable
          } as FileComparison);
        }
      });

      this.inProgress = false;
      this.table.renderRows();
    })).subscribe();
  }
  
  private getChangeType(rightFile, collFile)
  {
     if(!rightFile) {
       return 'deleted';
     }

     return (collFile.md5 === rightFile.md5)? 'no-change': 'modified';
  }

  ngOnDestroy(): void {
    console.log('on destroy CompareCollectionsDialog');
  }



  onCompareFileVersions(fileName: string, leftFileId: string, rightFileId: string) {
    this.dialog.open(CompareFilesDialog, {
      width: '80vw',
      height: '80vh',
      data: {
        title: `${fileName}`,
        leftFileId: leftFileId,
        rightFileId: rightFileId
      } as CompareFilesDialogData,
    });
  }



}
